import { useEffect, useState } from 'react';
import {
  AppConfigs,
  PrimaryStorage,
  UserMetaData,
} from '../../core/redux/auth/authTypes';
import {
  getAppConfigsLS,
  getUserMetaDataLS,
  removeUserMetaDataLS,
} from '../../core/utils/auth/authUtils';
import sessionStorageSingleton from '../../core/configs/sessionStorageSingleton';
import { useAppDispatch, useAppSelector } from '../../core/hooks/storeHooks';
import { ResultWithOptionalError } from '../../core/types/generic/results';
import { validateUserPassKey } from '../../core/database/user/multiUserStorage';
import { loadAndGetDirectoryPermission } from '../../core/database/manager/fileManager';
import { redirect, useNavigate } from 'react-router-dom';
import { initializeUser } from '../../core/redux/auth/thunks/user';
import { tabSharedWorker } from '../../core/sharedWorker/sharedWorkerManager';
import {
  SharedWorkerMessage,
  SharedWorkerResponse,
} from '../../core/sharedWorker/types';

export default () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userId = useAppSelector((state) => state.auth.user.id);
  const temporaryUser = useAppSelector((state) => state.auth.temporaryUser); // Will be used for switching temporarily

  const [isPermissionDialogOpen, togglePermissionDialog] = useState(false);
  const [isPassKeyModalOpen, togglePassKeyModal] = useState(false);
  const userMetaData: UserMetaData | null =
    temporaryUser ?? getUserMetaDataLS();
  const appConfigs: AppConfigs | null = getAppConfigsLS();

  const [userPrivateKey, setUserPrivateKey] = useState<CryptoKey | undefined>(
    undefined
  );

  useEffect(() => {
    document.title = 'Enigma';

    if (userId) redirect('/');
    else {
      const validateUser = async () => {
        if (userMetaData && appConfigs) {
          tabSharedWorker.port.postMessage({
            type: SharedWorkerMessage.AddToWaitingList,
            userId: userMetaData.id,
          });

          const result: ResultWithOptionalError<CryptoKey> =
            await validateUserPassKey(userMetaData.id, userMetaData.id);
          if (result.error || !result.data) {
            return togglePassKeyModal(true);
          }

          if (
            appConfigs.primaryStorage === PrimaryStorage.FileSystem ||
            appConfigs.localBackups
          ) {
            setUserPrivateKey(result.data);
            togglePermissionDialog(true);
          } else {
            await onPermissionAssigned(undefined, result.data);
          }
        }
      };

      validateUser();
    }
  }, []);

  // Temporary Worker Listener for verification from master tab
  // Will be override by the listener in userMasterTab once user verified
  useEffect(() => {
    if (!userId.length)
      tabSharedWorker.port.onmessage = (event: MessageEvent) => {
        const type = event.data.type;
        if (type === SharedWorkerResponse.ReceivePassKey) {
          const privateKey = event.data.privateKey;
          const directoryHandler = event.data.directoryHandler;
          const unSavedSheets = event.data.unSavedSheets;
          if (userMetaData && appConfigs) {
            dispatch(
              initializeUser({
                userMetaData: userMetaData,
                appConfigs: appConfigs,
                directoryHandler,
                privateKey,
                unSavedSheets
              })
            );
          }
        }
      };
  }, [userId]);

  const resetData = () => {
    togglePassKeyModal(false);
    togglePermissionDialog(false);
    setUserPrivateKey(undefined);
  };

  const onPermissionAssigned = async (
    directoryHandler?: FileSystemDirectoryHandle,
    privateKey?: CryptoKey
  ) => {
    // if (directoryHandler)
    //   sessionStorageSingleton.setDirectoryHandler(directoryHandler);
    privateKey = privateKey || userPrivateKey;

    if (privateKey && userMetaData && appConfigs) {
      dispatch(
        initializeUser({
          userMetaData: userMetaData,
          appConfigs: appConfigs,
          directoryHandler,
          privateKey,
        })
      );
      resetData();

      navigate('/');
    }
  };

  const validateStoragePermission = async () => {
    const result: ResultWithOptionalError<FileSystemDirectoryHandle> =
      await loadAndGetDirectoryPermission();

    if (result.data) {
      await onPermissionAssigned(result.data);
    }
  };

  const onPassKeyValidation = async (privateKey: CryptoKey) => {
    let dirHandler: FileSystemDirectoryHandle | undefined = undefined;
    if (
      appConfigs?.localBackups ||
      appConfigs?.primaryStorage === PrimaryStorage.FileSystem
    ) {
      const result: ResultWithOptionalError<FileSystemDirectoryHandle> =
        await loadAndGetDirectoryPermission();

      if (result.error) return;
      dirHandler = result.data;
    }

    await onPermissionAssigned(dirHandler, privateKey);
  };

  // const navigateAuth = () => navigate('/login');
  const handleLogout = () => {
    removeUserMetaDataLS();
    navigate('/login');
    // dispatch(logoutUser({ navigateAuth }));
  };

  return {
    userMetaData,
    appConfigs,
    isPassKeyModalOpen,
    onPassKeyValidation,
    isPermissionDialogOpen,
    validateStoragePermission,
    handleLogout,
  };
};
